<script lang="ts">
  import { Router, type RouteConfig } from "@mateothegreat/svelte5-router";
  import { loadNostrUser } from "@nostr/gadgets/metadata";
  import type { WindowNostr } from "@nostr/tools/nip07";

  import Forum from "./Forum.svelte";
  import Topic from "./Topic.svelte";
  import Link from "./components/Link.svelte";
  import { error } from "./error.svelte";
  import { info, labels } from "./relay.svelte";
  import WriteArea from "./components/WriteArea.svelte";
  import { getBasePath } from "./utils";

  const basePath = getBasePath();
  const routes: RouteConfig[] = [
    {
      path: basePath + "/",
      component: Forum
    },
    {
      path: basePath + "/c",
      component: Forum
    },
    {
      path: basePath + "/topic/(?<id>.*)",
      component: Topic
    }
  ];

  let logged = $state<string | null>(localStorage.getItem("logged_pubkey"));
  const loggedUser = $derived(logged ? loadNostrUser(logged) : null);

  $effect(() => {
    if (logged) {
      (window as any as Window & { nostr: WindowNostr }).nostr
        .getPublicKey()
        .then((pubkey) => {
          if (pubkey !== logged) logout();
        })
        .catch(logout);
    }
  });

  let cancel: any;
  $effect(() => {
    if (error.error) {
      clearTimeout(cancel);
      cancel = setTimeout(() => {
        error.error = null;
      }, 5000);
    }
  });

  function logout() {
    logged = null;
    window.localStorage.removeItem("logged_pubkey");
  }

  async function login() {
    try {
      let pubkey = await (
        window as any as Window & { nostr: WindowNostr }
      ).nostr.getPublicKey();

      logged = pubkey;
      window.localStorage.setItem("logged_pubkey", pubkey);
    } catch (err) {
      console.warn(err);
    }
  }
</script>

<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900">
  <!-- Sidebar -->
  <aside
    class="hidden w-64 flex-col justify-between border-r border-gray-200 bg-white px-8 py-8 lg:flex dark:border-gray-700 dark:bg-gray-800"
  >
    <div class="mb-8">
      <Link target="/" class="text-xl font-bold">{info.name}</Link>
    </div>

    {#if labels.length > 0}
      <nav>
        <div class="mb-4">
          <h3
            class="mb-2 text-sm font-medium text-gray-500 uppercase dark:text-gray-400"
          >
            Categories
          </h3>
          <ul class="space-y-2">
            {#each labels as label}
              <li>
                <Link
                  target={`/l/${label.toLowerCase().replace(/\s+/g, "-")}`}
                  class="flex items-center justify-between text-sm text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white"
                >
                  <span>{label}</span>
                </Link>
              </li>
            {/each}
          </ul>
        </div>
      </nav>
    {/if}

    <div class="mt-auto pt-8">
      {#if logged}
        <div class="flex items-center justify-between">
          <div>
            {#await loggedUser}
              <span class="text-sm">{logged.slice(0, 8)}…</span>
            {:then user}
              <span class="text-sm font-medium">{user!.shortName}</span>
            {/await}
          </div>
          <button
            onclick={logout}
            class="cursor-pointer text-sm text-red-800 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            Logout
          </button>
        </div>
      {:else}
        <button
          onclick={login}
          class="w-full rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
        >
          Login
        </button>
      {/if}
    </div>
  </aside>

  <!-- Main content -->
  <div class="flex-1">
    <header
      class="border-b border-gray-200 bg-white px-8 py-4 dark:border-gray-700 dark:bg-gray-800"
    >
      <div class="flex items-center justify-between">
        <div class="lg:hidden">
          <Link target="/" class="text-xl font-bold">{info.name}</Link>
        </div>
        <div class="flex items-center gap-4">
          <Link
            target="/"
            class="text-sm text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white"
          >
            Latest Topics
          </Link>
          <Link
            target="/c"
            class="text-sm text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white"
          >
            Latest Comments
          </Link>
        </div>
      </div>
    </header>

    <main class="px-4 py-8 lg:px-8">
      {#if error.error}
        <div
          class="mb-4 rounded bg-red-100 p-4 text-red-700 dark:bg-red-900/50 dark:text-red-300"
        >
          {error.error}
        </div>
      {/if}

      <Router {routes} />
    </main>
  </div>
</div>
<WriteArea />
